import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, Package, Route, ScrollText, User, Truck, ShieldAlert, CheckCircle2, XCircle, Lock, Unlock, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import AdminConversation from "@/components/admin/AdminConversation";
import { sizeLabel } from "@/lib/sizes";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ParcelDetail = () => {
  const { pid } = useParams();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openAction, setOpenAction] = useState(null); // "mark-delivered" | "cancel" | "release-payout" | "hold-payout" | "refund"
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/parcels/${pid}`);
      setD(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load parcel");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [pid]);

  const runAction = async () => {
    if (!reason.trim()) return toast.error("Reason required");
    setBusy(true);
    try {
      const body = { reason: reason.trim() };
      if (openAction === "refund" && amount) body.amount = Number(amount);
      await adminApi.post(`/parcels/${pid}/${openAction}`, body);
      toast.success(`${openAction.replace(/-/g, " ")} done`);
      setOpenAction(null);
      setReason(""); setAmount("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !d) return <div className="p-8 text-sm text-slate-500">Loading…</div>;
  const { parcel, sender, carrier, trip, trip_vehicle, delivery, distances, price_breakdown, totals, timeline } = d;
  const b = price_breakdown || {};

  return (
    <div className="p-8">
      <Link to="/admin/parcels" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="w-3 h-3"/> Back to parcels</Link>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-xl font-bold text-slate-900" data-testid="parcel-id"><Package className="w-5 h-5 inline mr-1"/> Parcel {parcel.id.slice(0, 8)}</h1>
            <Badge className="text-[10px] bg-slate-900 text-white">{(parcel.status || "").toUpperCase()}</Badge>
            <Badge className="text-[10px] bg-slate-100 text-slate-700">{sizeLabel(parcel)}</Badge>
            {parcel.category && parcel.category !== "general" && <Badge className="text-[10px] bg-amber-100 text-amber-700">{parcel.category}</Badge>}
          </div>
          <div className="text-xs text-slate-500 mt-1">Posted {parcel.created_at ? new Date(parcel.created_at).toLocaleString() : ""}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Sender total</div>
          <div className="font-display font-bold text-2xl tabular-nums" data-testid="sender-total">{totals.sender_total ? `₹${totals.sender_total.toFixed(2)}` : "—"}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card title={<><User className="w-4 h-4 inline mr-1"/> Sender</>}>
          {sender ? (
            <>
              <Row label="Name" value={<Link to={`/admin/users/${sender.id}`} className="text-slate-900 hover:underline">{sender.name}</Link>}/>
              <Row label="Phone" value={sender.phone}/>
              <Row label="Trust tier" value={(sender.trust_tier || "new").toUpperCase()}/>
              <Row label="Rating" value={sender.rating_avg ? sender.rating_avg.toFixed(1) : "—"}/>
              <Row label="Deliveries" value={sender.delivery_count || 0}/>
            </>
          ) : <em className="text-slate-400 text-xs">Not available</em>}
          <Line/>
          <Row label="Declared value" value={parcel.declared_value ? `₹${parcel.declared_value.toFixed(2)}` : "—"}/>
          <Row label="Declared contents" value={parcel.description || "—"}/>
          <Row label="Insurance opted?" value={parcel.insurance ? "Yes" : "No"}/>
        </Card>

        <Card title={<><Truck className="w-4 h-4 inline mr-1"/> Carrier & trip</>}>
          {carrier ? (
            <>
              <Row label="Name" value={<Link to={`/admin/users/${carrier.id}`} className="text-slate-900 hover:underline">{carrier.name}</Link>}/>
              <Row label="Phone" value={carrier.phone}/>
              <Row label="Trust tier" value={(carrier.trust_tier || "new").toUpperCase()}/>
              <Row label="Rating" value={carrier.rating_avg ? carrier.rating_avg.toFixed(1) : "—"}/>
              <Row label="Deliveries" value={carrier.delivery_count || 0}/>
            </>
          ) : <em className="text-slate-400 text-xs">No carrier yet</em>}
          <Line/>
          {trip_vehicle
            ? <>
                <Row label="Vehicle type" value={trip_vehicle.type?.toUpperCase()}/>
                {trip_vehicle.make_model && <Row label="Make & model" value={trip_vehicle.make_model}/>}
                {trip_vehicle.reg_no && <Row label="Reg no." value={trip_vehicle.reg_no}/>}
              </>
            : <div className="text-xs text-slate-400 italic">No vehicle recorded on trip</div>
          }
          <Row label="Trip mode" value={trip?.travel_mode?.toUpperCase()}/>
          <Row label="Trip date" value={trip?.date}/>
        </Card>

        <Card title={<><Route className="w-4 h-4 inline mr-1"/> Route & distances</>}>
          <Row label="Pickup" value={parcel.pickup_location}/>
          <Row label="Pickup details" value={parcel.pickup_details || <em className="text-slate-400">—</em>}/>
          <Row label="Drop" value={parcel.drop_location}/>
          <Row label="Drop details" value={parcel.drop_details || <em className="text-slate-400">—</em>}/>
          <Line/>
          <Row label="Parcel route (km)" value={distances.parcel_route_km || 0}/>
          <Row label="Carrier trip alone (A)" value={distances.trip_route_km ? `${distances.trip_route_km} km` : "—"}/>
          <Row label="Trip + parcel (B)" value={distances.trip_with_parcel_km ? `${distances.trip_with_parcel_km} km` : "—"}/>
          <Row label="Detour (B − A)" value={distances.detour_km ? <span className="text-slate-900 font-medium">{distances.detour_km} km</span> : "—"}/>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card title="Price breakdown">
          <PriceRow label="Handover fee" value={b.handover_fee}/>
          {b.route_component !== undefined ? (
            <>
              <PriceRow label={b.mode_kind === "transit" ? `Route · bus/train tier fee (${b.route_km || 0} km)` : `Route (${b.route_km || 0} km × ₹${b.route_rate || 0}/km)`} value={b.route_component}/>
              <PriceRow label={`Detour (${b.detour_km || 0} km × ₹${b.detour_rate || 0}/km)`} value={b.detour_component}/>
            </>
          ) : (
            <PriceRow label={`Distance (${b.detour_km || 0} km)`} value={b.distance_component}/>
          )}
          <PriceRow label={`Size fee (${Math.round((b.size_uplift_pct || 0) * 100)}% of distance)`} value={b.size_component}/>
          <PriceRow label="Protection fee" value={b.insurance_component}/>
          <Line/>
          <PriceRow label="Carrier payout" value={totals.carrier_payout} bold/>
          <PriceRow label="Platform commission" value={totals.commission}/>
          <PriceRow label="Sender total" value={totals.sender_total} big/>
          {(b.min_floor_applied || b.min_earnings_applied) && <div className="mt-2 text-[11px] text-emerald-700" data-testid="floor-applied">Minimum earnings floor applied (₹{b.min_floor} for {b.floor_basis_km ?? "—"} km)</div>}
        </Card>

        <Card title="Payment & payout">
          <Row label="Sender total" value={totals.sender_total ? `₹${totals.sender_total.toFixed(2)}` : "—"}/>
          <Row label="Carrier payout" value={totals.carrier_payout ? `₹${totals.carrier_payout.toFixed(2)}` : "—"}/>
          <Row label="Commission" value={totals.commission ? `₹${totals.commission.toFixed(2)}` : "—"}/>
          <Row label="Payment received, payout pending?" value={delivery?.payment_held ? "YES" : "NO"}/>
          <Row label="Payment status" value={d.payment_status || "n/a"}/>
          <Row label="Drop PIN entered?" value={d.drop_pin_entered ? "Yes" : "No"}/>
          <Row label="Delivered at" value={d.delivered_at ? new Date(d.delivered_at).toLocaleString() : "Not delivered"}/>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <CallLogCard pid={parcel.id}/>

        <Card title="Pickup photo">
          {d.pickup_photo_url ? (
            <div>
              <img src={`${BACKEND}${d.pickup_photo_url}`} alt="Pickup" className="rounded-lg max-h-[240px]" data-testid="pickup-photo"/>
              <div className="text-[11px] text-slate-500 mt-2">Uploaded at {d.pickup_photo_at ? new Date(d.pickup_photo_at).toLocaleString() : "—"}</div>
            </div>
          ) : <div className="text-xs text-slate-400 italic">No pickup photo yet.</div>}
        </Card>

        <Card title={<><ScrollText className="w-4 h-4 inline mr-1"/> Timeline</>}>
          {(!timeline || timeline.length === 0) && <div className="text-xs text-slate-400 italic">No timeline yet — parcel is still a draft.</div>}
          <ol className="space-y-2">
            {(timeline || []).map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-xs">
                <span className="w-2 h-2 rounded-full bg-slate-900 mt-1.5"/>
                <div>
                  <div className="font-medium capitalize">{t.status?.replace(/_/g, " ")}</div>
                  <div className="text-slate-500">{new Date(t.at).toLocaleString()}</div>
                </div>
              </li>
            ))}
          </ol>
        </Card>
      </div>

      <div className="rounded-2xl border-2 border-red-200 bg-red-50/30 p-4" data-testid="admin-actions">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert className="w-5 h-5 text-red-600"/>
          <h2 className="font-display font-bold text-red-900">Admin actions</h2>
          <span className="text-[11px] text-red-700">Every action is written to the audit log.</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          <ActionBtn testid="act-mark-delivered" disabled={!delivery || delivery.status === "delivered"} onClick={() => setOpenAction("mark-delivered")} icon={CheckCircle2} tone="emerald">Mark delivered</ActionBtn>
          <ActionBtn testid="act-release" disabled={!delivery?.payment_held} onClick={() => setOpenAction("release-payout")} icon={Unlock} tone="blue">Release payout</ActionBtn>
          <ActionBtn testid="act-hold" disabled={!delivery || delivery.status === "delivered"} onClick={() => setOpenAction("hold-payout")} icon={Lock} tone="amber">Hold payout</ActionBtn>
          <ActionBtn testid="act-refund" disabled={!delivery} onClick={() => setOpenAction("refund")} icon={RefreshCcw} tone="violet">Refund sender</ActionBtn>
          <ActionBtn testid="act-cancel" disabled={parcel.status === "cancelled"} onClick={() => setOpenAction("cancel")} icon={XCircle} tone="red">Cancel parcel</ActionBtn>
        </div>
      </div>

      <AdminConversation parcelId={parcel.id}/>

      <Dialog open={!!openAction} onOpenChange={(v) => { if (!v) { setOpenAction(null); setReason(""); setAmount(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="capitalize">{(openAction || "").replace(/-/g, " ")}</DialogTitle>
            <DialogDescription>
              You are about to <b>{(openAction || "").replace(/-/g, " ")}</b> parcel <span className="font-mono">{parcel.id.slice(0, 8)}</span>. This affects money movement — please explain why in the reason box. Written to the audit log.
            </DialogDescription>
          </DialogHeader>
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Reason (required)</label>
          <Textarea data-testid="action-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="e.g. Parties confirmed handover by phone, but PIN was never entered"/>
          {openAction === "refund" && (
            <div className="mt-2">
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Amount (₹). Leave blank to refund the sender's full total.</label>
              <Input data-testid="action-amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={totals.sender_total ? String(totals.sender_total) : "0"}/>
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpenAction(null)}>Cancel</Button>
            <Button data-testid="btn-confirm-action" disabled={!reason.trim() || busy} onClick={runAction} className="bg-red-600 hover:bg-red-700">Confirm</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ActionBtn = ({ testid, icon: Icon, tone, disabled, onClick, children }) => {
  const tones = {
    emerald: "border-emerald-200 text-emerald-700 hover:bg-emerald-50",
    blue: "border-blue-200 text-blue-700 hover:bg-blue-50",
    amber: "border-amber-200 text-amber-700 hover:bg-amber-50",
    violet: "border-violet-200 text-violet-700 hover:bg-violet-50",
    red: "border-red-200 text-red-700 hover:bg-red-50",
  };
  return (
    <button
      data-testid={testid}
      disabled={disabled}
      onClick={onClick}
      className={`rounded-xl border-2 bg-white p-3 text-xs font-medium flex flex-col items-center gap-1 transition disabled:opacity-40 disabled:cursor-not-allowed ${tones[tone]}`}
    >
      <Icon className="w-4 h-4"/>
      {children}
    </button>
  );
};

const CallLogCard = ({ pid }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { adminApi.get("/calls", { params: { parcel_id: pid } }).then((r) => setRows(r.data)).catch(() => setRows([])); }, [pid]);
  return (
    <Card title="Call attempts">
      {!rows || rows.length === 0 ? <div className="text-xs text-slate-400" data-testid="call-log-empty">No call attempts.</div> : (
        <div className="space-y-1" data-testid="call-log-list">
          {rows.map((r) => (
            <div key={r.id} className="flex justify-between text-xs border-b border-slate-100 py-1">
              <span>{r.initiated_by_name || r.initiated_by.slice(0, 8)} <span className="text-slate-400">({r.initiated_by_role})</span> · {r.status}</span>
              <span className="text-slate-500 tabular-nums">{new Date(r.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const Card = ({ title, children }) => (
  <div className="rounded-2xl bg-white border border-slate-200 p-4">
    <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-medium">{title}</div>
    <div className="space-y-0.5">{children}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-1 text-sm gap-2">
    <span className="text-slate-500 text-xs">{label}</span>
    <span className="text-slate-900 text-right text-xs">{value ?? <em className="text-slate-400">—</em>}</span>
  </div>
);

const PriceRow = ({ label, value, bold, big }) => {
  const v = value === 0 || value ? `₹${Number(value).toFixed(2)}` : null;
  return (
    <div className="flex items-center justify-between py-1 gap-2">
      <span className={`text-xs ${bold ? "font-medium text-slate-900" : "text-slate-500"}`}>{label}</span>
      <dd className={`tabular-nums ${big ? "text-lg font-display font-bold text-slate-900" : bold ? "font-medium text-slate-900" : "text-slate-700 text-sm"}`}>{v || <span className="text-slate-400 italic">Not available</span>}</dd>
    </div>
  );
};

const Line = () => <hr className="my-1 border-slate-200"/>;

export default ParcelDetail;
